import { useState, useEffect } from "react";
import { ColorWheelRoot } from "./ColorWheel.styles";
import Hue from "@uiw/react-color-hue";
import Saturation from "@uiw/react-color-saturation";
import { rgbaToHsva, hsvaToRgba } from "@uiw/color-convert";

/* ---------- helpers: Float RGB <-> RGBA(0-255) ---------- */

const clamp01 = (n) => Math.min(1, Math.max(0, Number(n)));

// r=0.123,g=0.456,b=0.789,a=1
function parseKeyedFloatRgba(str) {
  if (typeof str !== "string") return null;
  const m = str
    .replace(/\s+/g, "")
    .match(/^r=(\d*\.?\d+),g=(\d*\.?\d+),b=(\d*\.?\d+)(?:,a=(\d*\.?\d+))?$/i);
  if (!m) return null;
  const r = clamp01(m[1]);
  const g = clamp01(m[2]);
  const b = clamp01(m[3]);
  // alpha optional; default 1
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: 1,
  };
}

// "0.12,0.34,0.56"
function parseFloatRgbString(str) {
  if (typeof str !== "string") return null;
  const parts = str.split(",").map((p) => clamp01(p.trim()));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [r, g, b] = parts;
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: 1,
  };
}

function parseFloatRgbObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (!("r" in obj && "g" in obj && "b" in obj)) return null;
  const r = clamp01(obj.r),
    g = clamp01(obj.g),
    b = clamp01(obj.b);
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
    a: 1,
  };
}

function rgba255ToFloatDescriptor({ r, g, b }) {
  const f = (v) => (v / 255).toFixed(3); // normalized float with 3 decimals
  return `r=${f(r)},g=${f(g)},b=${f(b)},a=1`;
}

/* -------------------- Component -------------------- */

export const ColorWheel = ({ currentColor, onCurrentColorChange }) => {
  const [hsva, setHsva] = useState({ h: 0, s: 0, v: 0, a: 1 });

  // Prop -> HSVA
  useEffect(() => {
    // Already an HSVA object?
    if (
      currentColor &&
      typeof currentColor === "object" &&
      "h" in currentColor
    ) {
      setHsva({
        h: currentColor.h ?? 0,
        s: currentColor.s ?? 0,
        v: currentColor.v ?? 0,
        a: currentColor.a ?? 1,
      });
      return;
    }

    // 1) UE keyed float string: r=...,g=...,b=...,a=...
    const keyed = parseKeyedFloatRgba(currentColor);
    if (keyed) {
      setHsva(rgbaToHsva(keyed));
      return;
    }

    // 2) plain "r,g,b" floats (0..1)
    const fromFloatStr = parseFloatRgbString(currentColor);
    if (fromFloatStr) {
      setHsva(rgbaToHsva(fromFloatStr));
      return;
    }

    // 3) {r,g,b} floats (0..1)
    const fromFloatObj = parseFloatRgbObject(currentColor);
    if (fromFloatObj) {
      setHsva(rgbaToHsva(fromFloatObj));
      return;
    }
  }, [currentColor]);

  const emitFloatDescriptor = (nextHsva) => {
    const rgba255 = hsvaToRgba(nextHsva); // {r,g,b,a} in 0..255
    const descriptorStr = rgba255ToFloatDescriptor(rgba255);
    onCurrentColorChange?.(descriptorStr, nextHsva);
  };

  const handleSaturationChange = (sat) => {
    const next = { ...hsva, ...sat, a: hsva.a };
    setHsva(next);
    emitFloatDescriptor(next);
  };

  const handleHueChange = ({ h }) => {
    const next = { ...hsva, h };
    setHsva(next);
    emitFloatDescriptor(next);
  };

  return (
    <ColorWheelRoot>
      <Hue className="color-hue" hue={hsva.h} onChange={handleHueChange} />
      <Saturation
        className="color-saturation"
        hsva={hsva}
        onChange={handleSaturationChange}
      />
    </ColorWheelRoot>
  );
};
